import { useCallback, useImperativeHandle, useRef } from 'react';
import * as React from 'react';
import tw, { styled } from 'twin.macro';
import 'styled-components/macro';

export interface TextBoxProps
  extends Omit<React.ComponentPropsWithoutRef<'input'>, OmittedInputProps>,
    Pick<React.ComponentPropsWithoutRef<'div'>, PickedDivProps> {
  hasError?: boolean;

  /**
   * Reference to the underlying HTML input element.
   */
  inputRef?: React.Ref<HTMLInputElement | null>;

  /**
   * Content (usually an icon, 16x16 ones work best) to place after the text
   * box's input area. Screen readers will skip it.
   */
  postfix?: React.ReactNode;

  /**
   * Same as {@link TextBoxProps.postfix}, but before the text box's input area.
   */
  prefix?: React.ReactNode;
}

type OmittedInputProps = 'onClick' | 'prefix';
type PickedDivProps = 'onClick';

export function TextBox({
  className = '',
  disabled,
  inputRef: inputRefFromProps,
  onClick,
  postfix,
  prefix,
  style,
  hasError,
  ...forwardedProps
}: TextBoxProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      inputRef.current?.focus();
      onClick?.(event);
    },
    [onClick],
  );

  useImperativeHandle(inputRefFromProps, () => inputRef.current);

  return (
    <Container
      $disabled={disabled}
      $hasError={hasError}
      className={`group ${className}`}
      onClick={handleClick}
      style={style}
    >
      {prefix && <AffixContainer aria-hidden>{prefix}</AffixContainer>}
      <StyledInput
        ref={inputRef}
        $hasPostfix={!!postfix}
        $hasPrefix={!!prefix}
        disabled={disabled}
        {...forwardedProps}
        value={forwardedProps.value === null ? '' : forwardedProps.value}
      />
      {postfix && <AffixContainer aria-hidden>{postfix}</AffixContainer>}
    </Container>
  );
}

const Container = styled.div<{
  $disabled?: boolean;
  $hasError?: boolean;
}>(({ $disabled, $hasError }) => [
  tw`
    bg-white
    text-primary-medium
    border-1
    border-gray-15
    box-border
    cursor-text
    flex
    px-2
    py-1
    rounded-sm
    transition duration-fast
  `,
  $disabled && tw`pointer-events-none bg-white border-gray-15 text-gray-70`,
  $hasError
    ? tw`border-error ring ring-error-lightest`
    : tw`focus-within:(border-secondary ring ring-secondary-lighter)`,
]);

const StyledInput = styled.input<{
  $hasPostfix?: boolean;
  $hasPrefix?: boolean;
}>(({ $hasPostfix, $hasPrefix }) => [
  tw`
    bg-transparent
    flex-grow
    text-lg
    leading-snug
    text-primary-medium

    // So the input shrinks past its default width
    w-0

    placeholder:text-gray-50
    focus:(outline-none text-indigo)
    group-hover:text-indigo
    disabled:text-gray-30
  `,
  {
    '&::-webkit-search-cancel-button, &::-webkit-search-decoration': tw`appearance-none`,
  },
  $hasPostfix && tw`pr-1`,
  $hasPrefix && tw`pl-1`,
]);

const AffixContainer = tw.span`flex flex-shrink-0 items-center justify-center`;
